import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, concatMap, EMPTY, from, of, switchMap, withLatestFrom } from 'rxjs';
import { UserActions } from '../state/user/user.actions';
import { Store } from '@ngrx/store';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ACCESS_TOKEN_KEY, LocalStorageService } from '@ui';
import { Router } from '@angular/router';
import { routerSegments } from '../../consts/routes';
import { AuthActions } from '../state/auth/auth.action-types';
import { userFeature } from '../state/user/user.feature';

export const setStoreSession = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(UserActions.loginSuccess),
      switchMap(({ data }) => {
        authService.storeSession(data);
        return of(AuthActions.startLoadRequiredData());
      }),
    );
  },
  { functional: true },
);

export const getUserProfile = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    authService = inject(AuthService),
    userService = inject(UserService),
    localStorageService = inject(LocalStorageService),
  ) => {
    return actions$.pipe(
      ofType(UserActions.getUserProfile),
      withLatestFrom(store.select(userFeature.selectProfile)),
      switchMap(([, userProfile]) => {
        const isLoggedIn = authService.isLoggedIn();
        const accessToken =
          localStorageService.getItemWithCheck(ACCESS_TOKEN_KEY);

        // if not logged in OR already loaded -> don't hit backend
        if (!isLoggedIn || !!userProfile) {
          return EMPTY;
        }
        if (!accessToken) {
          return EMPTY;
        }
        return userService.getUserProfile(accessToken).pipe(
          switchMap((profile) => [
            UserActions.getUserProfileSuccess({ profile }),
            AuthActions.requiredDataLoadedSuccess(),
          ]),
          catchError(() => of(UserActions.getUserProfileFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const redirectAfterProfileLoaded = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(UserActions.loginSuccess),
      switchMap(({ redirectUrl }) =>
        from(router.navigateByUrl(redirectUrl ?? routerSegments.home)),
      ),
    ),
  { functional: true, dispatch: false },
);

export const logout = createEffect(
  (actions$ = inject(Actions), auth = inject(AuthService)) =>
    actions$.pipe(
      ofType(AuthActions.logout),
      concatMap(({ params }) => {
        auth.logout(params);
        return concat(of(UserActions.resetToInitialState()));
      }),
    ),
  { functional: true },
);
